<template>
  <div
    :class="componentClass"
    :style="componentStyle"
  >
    <wa-tag
      v-if="shouldShowSaleTag"
      theme="yellow"
      label="On Sale!"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="isExpiredPlan && !isInModalContext"
      theme="gray inverted"
      label="Your Expired Plan"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="isCurrentPlan && !isInModalContext"
      theme="blue"
      label="Current Plan"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="plan.mostPopular && !isInModalContext && !shouldShowSaleTag"
      theme="yellow"
      label="Most Popular"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="plan.name === 'Pro+' && !isInModalContext && !shouldShowSaleTag"
      theme="teal"
      label="Best Value"
      position="top"
      size="size-sm"
    />

    <wa-tag
      v-else-if="!plan.proDownloadEnabled && !isInModalContext"
      theme="gravy"
      label="Online Only"
      position="top"
      size="size-sm"
    />

    <h2 class="h1 margin-bottom-2xs">
      <span :style="`color: var(--fa-${planColor});`">
        <font-awesome-icon
          :icon="$v7[planIconStyle][icon]"
          size="lg"
          class="decorative display-block fa-width-auto"
        />
      </span>
      <span class="text-nowrap size-md tablet:size-sm laptop:size-md"
        ><span v-if="shortName">{{ shortName }}</span
        ><span v-else>{{ plan.name }}</span></span
      >
    </h2>

    <div style="flex-grow: 1; flex-shrink: 0; flex-basis: auto">
      <template v-if="isInModalContext">
        <h3 class="sr-only">This Plan Includes&hellip;</h3>
        <ul
          class="fa-ul margin-y-0 display-flex flex-column"
          style="gap: var(--spacing-4xs)"
        >
          <li
            v-if="planHasProPlusIcons"
            class="b"
          >
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v7.solid.faCircleSmall"
                size="xs"
              />
            </span>
            Includes all Icon Packs
          </li>

          <li :class="featureListItemClass('pro_icon')">
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v7.solid.faCircleSmall"
                size="xs"
              />
            </span>
            Access to all Pro<template v-if="planHasProPlusIcons"> and Pro+</template> Icons
          </li>

          <li :class="featureListItemClass('custom_icon')">
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v7.solid.faCircleSmall"
                size="xs"
              />
            </span>
            <template v-if="plan.customIcons === -1"><font-awesome-icon :icon="$v7.regular.faInfinity" /><span class="sr-only">Unlimited </span></template>
            <template v-else>{{ plan.customIcons }}</template>
            Custom icons
          </li>

          <li
            v-if="planHasProDownload"
            :class="featureListItemClass('download')"
          >
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v7.solid.faCircleSmall"
                size="xs"
              />
            </span>
            Download Pro<template v-if="planHasProPlusIcons"> and Pro+</template> icons
          </li>

          <li :class="featureListItemClass('service')">
            <span class="fa-li">
              <font-awesome-icon
                :icon="$v7.solid.faCircleSmall"
                size="xs"
              />
            </span>
            Extra Services
          </li>
        </ul>

        <p
          v-if="planIsOnlineOnly"
          class="margin-top-lg"
        >
          Online only via Kits on the web &mdash; No icon download, npm, or Figma
        </p>
      </template>

      <template v-else>
        <h3 class="sr-only">About This Plan</h3>
        <slot name="alt-description"></slot>
        <p
          v-if="!$slots['alt-description']"
          class="margin-bottom-0"
        >
          {{ plan.description }}
        </p>
      </template>
    </div>

    <div>
      <template v-if="isShowingDiscount">
        <div class="row roomy align-middle align-center margin-y-md">
          <div
            v-if="currentDollars !== planPrice"
            class="strong text-center"
            style="color: var(--fa-gravy); --padding-right: var(--spacing-xs); --padding-left: var(--spacing-xs)"
          >
            <span class="sr-only">Regular price:</span>
            <span class="size-4xl tablet:size-3xl laptop:size-4xl"
              ><s>{{ planPrice }}<span class="sr-only">/year</span></s></span
            >
          </div>
          <div
            class="strong"
            style="--padding-right: var(--spacing-xs); --padding-left: var(--spacing-xs)"
          >
            <span class="sr-only">Current price:</span>
            <span class="display-flex align-start align-center">
              <span class="size-4xl tablet:size-3xl laptop:size-4xl">{{ currentDollars }}</span>
              <span
                v-if="currentCents"
                class="size-lg padding-top-2xs"
                >.{{ currentCents }}</span
              >
            </span>
            <span class="sr-only"
              >{{ priceDurationLabel }}<span v-if="discountDuration === 'once'">, then {{ planPrice }}/year</span></span
            >
          </div>
        </div>
        <p
          class="text-center margin-bottom-lg"
          style="color: var(--fa-md-gravy)"
          :aria-label="priceDurationDescription"
          data-balloon-pos="up"
          data-balloon-length="fit"
        >
          <font-awesome-icon
            :icon="$v7.solid.faCalendarDays"
            size="lg"
            class="decorative margin-right-2xs"
          />
          {{ priceDurationLabel }}
        </p>
      </template>

      <div
        v-else
        class="text-center"
      >
        <p class="size-4xl strong margin-y-4xs">
          {{ currentDollars }}<span v-if="currentCents">.{{ currentCents }}</span>
        </p>
        <p
          class="text-center margin-bottom-xl"
          style="color: var(--fa-md-gravy)"
          aria-label="Renews each year"
          data-balloon-pos="up"
        >
          <font-awesome-icon
            :icon="$v7.solid.faCalendarDays"
            size="lg"
            class="decorative margin-right-2xs"
          />
          Billed Annually
        </p>
      </div>

      <span
        v-if="isCurrentPlan && !isExpiredPlan"
        :class="`button flat size-lg block margin-y-0 ${planColor} inverted`"
        style="pointer-events: none"
        >Your Plan!</span
      >

      <span
        v-else-if="isDowngrade"
        class="button flat size-lg block margin-y-0"
        aria-hidden="true"
        style="opacity: 0; pointer-events: none"
      >
        Contact Us
      </span>

      <site-link
        v-else
        :class="`button size-lg block margin-bottom-0 ${planColor}`"
        :to="planLinkArguments(plan)"
        @click.native="trackThen($event, isInModalContext)"
      >
        Get {{ plan.name }}
      </site-link>
    </div>
  </div>
</template>

<script>
  import accounting from 'accounting'
  import { get } from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import {
    shouldHavePromoCode,
    getDiscountPercent,
    PLAN_COLOR_BY_SKU,
    PLAN_ICONSTYLE_BY_SKU,
    getAutoAppliedDiscounts,
    getKickstarterDiscounts,
    getPromoCodes
  } from '../PlansData.js'
  import { EventTrackingMixin } from 'app/mixins'
  import UtilsMixin from 'app/views/UtilsMixin'
  import { priceWithDiscount, doesDiscountApplyToPlan, formatTotalCurrency, discountRepeatingYears } from 'app/views/plans/priceUtils'

  export default {
    name: 'PlanSummaryCard',

    mixins: [UtilsMixin, EventTrackingMixin],

    props: {
      altDescription: {
        type: String
      },
      customStyle: {
        type: Object,
        default: () => {}
      },
      modalContextSlug: {
        type: String
      },
      hasBorder: {
        type: Boolean,
        default: false
      },
      isInModalContext: {
        type: Boolean,
        default: false
      },
      isLast: {
        type: Boolean,
        default: true
      },
      isShowingTag: {
        type: Boolean,
        default: true
      },
      isShowingDescription: {
        type: Boolean,
        default: true
      },
      sku: {
        type: String
      },
      shortName: {
        type: String
      }
    },

    data() {
      return {
        currentDiscount: null,
        isShowingAboutProPlusModal: false,
        plan: null,
        plusChecked: false
      }
    },

    computed: {
      ...mapGetters('accounts', ['currentAccount']),
      ...mapGetters('plans', ['currentPlans', 'getPlan', 'subscriptionDiscounts']),
      ...mapGetters('subscriptions', ['currentSubscription']),
      ...mapGetters('discounts', ['globalDiscountCode']),

      buyRoute() {
        return `plans.${this.plan.slug}`
      },

      componentClass() {
        return [
          'plan-card-summary card roomy text-left display-flex flex-column padding-top-2xl with-top-tag with-border',
          { 'margin-bottom-3xl tablet:margin-bottom-0': !this.isLast && !this.isInModalContext },
          { 'margin-bottom-3xl laptop:margin-bottom-0': !this.isLast && this.isInModalContext },
          { 'plan-on-sale': this.shouldShowSaleTag }
        ]
      },

      currentSubName() {
        return get(this.currentSubscription, ['plan', 'name'], null)
      },

      discountPercent() {
        return getDiscountPercent(this.currentAccount, this.currentSubscription)
      },

      componentStyle() {
        let style = {}

        style['--card-roomy-vertical-padding'] = 'var(--spacing-2xl)'
        style['--card-roomy-horizontal-padding'] = 'var(--spacing-2xl)'
        style['--card-margin-bottom'] = '0'
        style['flex'] = '1'

        if (this.isInModalContext) {
          style['--border-width'] = 'var(--border-width-sm)'
        }

        return {
          ...style,
          ...this.customStyle
        }
      },

      icon() {
        return get(this.plan, ['iconName'], 'faMoon')
      },

      planColor() {
        return get(PLAN_COLOR_BY_SKU, [this.plan.sku], 'yellow')
      },

      planIconStyle() {
        return get(PLAN_ICONSTYLE_BY_SKU, [this.plan.sku], 'solid')
      },

      isCurrentPlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)

        return this.plan.sku === currentPlanSku
      },

      isDowngrade() {
        const currentPlan = get(this.currentSubscription, ['plan'], null)
        const currentSubscriptionStatus = get(this.currentSubscription, ['status'], null)

        if (currentSubscriptionStatus === 'canceled') {
          return currentPlan ? !get(currentPlan, ['allowedCanceledPurchases'], []).includes(get(this.plan, ['sku'], 'NOPLAN')) : false
        }

        return currentPlan ? !get(currentPlan, ['allowedUpgrades'], []).includes(get(this.plan, ['sku'], 'NOPLAN')) : false
      },

      isExpiredPlan() {
        const currentPlanSku = get(this.currentSubscription, ['plan', 'sku'], null)
        const subStatusCancelled = get(this.$subscription, ['status']) === 'canceled'

        if (this.plan.sku === currentPlanSku && subStatusCancelled) {
          return true
        } else {
          return false
        }
      },

      currentPrice() {
        let price = get(this.plan, ['price', 'amount'], 0)

        if (this.currentDiscount) {
          const discountedPrice = priceWithDiscount(this.plan, price, this.currentDiscount)

          price = discountedPrice
        }

        return formatTotalCurrency(price / 100)
      },

      isShowingDiscount() {
        return this.subscriptionDiscounts.length > 0
      },

      planHasProPlusIcons() {
        return this.plan?.iconCollection?.name === 'pro-plus-collection'
      },

      planHasProDownload() {
        return this.plan?.proDownloadEnabled
      },

      planIsOnlineOnly() {
        return !this.plan?.proDownloadEnabled
      },

      discountDuration() {
        return get(this.currentDiscount, 'duration', null)
      },

      planPrice() {
        return accounting.formatMoney(get(this.plan, ['price', 'amount'], 0) / 100, null, 0)
      },

      currentDollars() {
        return this.currentPrice[0]
      },

      currentCents() {
        return this.currentPrice[1]
      },

      priceDurationLabel() {
        if (this.discountDuration === 'repeating') {
          return `${discountRepeatingYears(this.currentDiscount)} Year Discount`
        } else if (this.discountDuration === 'once') {
          return 'First Year Discount'
        } else if (this.discountDuration === 'forever') {
          return 'Locked-in Annual Discount'
        } else {
          return 'Billed Annually'
        }
      },

      priceDurationDescription() {
        if (this.discountDuration === 'repeating') {
          return `Enjoy a special deal for ${discountRepeatingYears(this.currentDiscount)} years!`
        } else if (this.discountDuration === 'once') {
          return 'Enjoy a special deal for your first year!'
        } else if (this.discountDuration === 'forever') {
          return "Keep saving as long as you're subscribed!"
        } else {
          return 'Renews each year'
        }
      },

      shouldShowSaleTag() {
        return this.currentDiscount ? doesDiscountApplyToPlan(this.plan, this.currentDiscount) : false
      }
    },

    async created() {
      const data = [this.fetchPlans()]
      this.plan = this.planBySku(this.sku)

      if (this.subscriptionDiscounts.length > 1) {
        this.currentDiscount = this.subscriptionDiscounts.find((discount) => get(discount, ['appliesTo', 'products'], []).includes(this.plan.sku))
      } else if (this.subscriptionDiscounts.length === 1 && doesDiscountApplyToPlan(this.plan, this.subscriptionDiscounts[0])) {
        this.currentDiscount = this.subscriptionDiscounts[0]
      }

      if (shouldHavePromoCode(this.currentAccount, this.currentSubscription)) {
        data.push(this.fetchDiscounts([getPromoCodes()[this.discountPercent]['id']]))
        this.promoCode = getPromoCodes()[this.discountPercent]['code']
      } else if (this.globalDiscountCode) {
        data.push(this.fetchDiscounts([this.globalDiscountCode]))
      } else if (getKickstarterDiscounts().length > 0 && this.isBackerWithoutActiveSubscription) {
        data.push(this.fetchDiscounts(getKickstarterDiscounts()))
      } else if (getAutoAppliedDiscounts().length > 0) {
        data.push(this.fetchDiscounts(getAutoAppliedDiscounts()))
      }

      try {
        await Promise.all(data)
      } finally {
        this.isLoading = false
      }
    },

    methods: {
      ...mapActions('plans', ['fetchPlans', 'fetchDiscounts']),

      featureListItemClass(feature_slug) {
        return this.modalContextSlug.includes(feature_slug) ? 'b' : null
      },

      trackThen(event, isInModalContext) {
        if (isInModalContext) {
          this.purchaseFlowEvent('get_pro_button_click', {
            context: 'upsell_modal',
            feature: this.modalContextSlug
          })
        }
      },

      planBySku(sku) {
        const plan = this.currentPlans.find((plan) => plan.sku == sku)

        if (!plan) {
          throw new Error(`Cannot find a plan with SKU "${sku}" in currentPlans.`)
        }

        return plan
      },

      planLinkArguments(plan) {
        if (this.currentDiscount) {
          if (doesDiscountApplyToPlan(plan, this.currentDiscount)) {
            return { name: this.buyRoute, query: { discount: this.currentDiscount.promoCode } }
          }
        }

        return { name: this.buyRoute }
      }
    }
  }
</script>
